import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import t from "../../../app/i18n";
import { agentApi } from "../../../modules/agent/api";
import {
  Agent,
  AgentBrokerNumber,
  AgentCompetence,
  AgentCompetenceFilterPageRequest,
  AgentFilterExportRequest,
  AgentFilterPageRequest,
  AgentLicense,
  AgentPartnerIdNumberRequest,
  AgentSearchRequest,
  AgentTreeFilterRequest,
  CreateUpdateAgent,
  CreateUpdateAgentBranding,
  CreateUpdateAgentBrokerNumber,
  CreateUpdateAgentCompetence,
  CreateUpdateAgentLicense,
  CreateUpdateSecondaryAgent,
  UpdateAgentParent,
} from "../../../modules/agent/types";
import { sortAgentBrokerNumbers, sortAgentCompetences, sortAgentLicenses } from "../../../modules/agent/utils";
import { EnumerationType } from "../../../modules/enumerations/enums";
import { UUID } from "../../../typings/global";
import { EntityIdObject, EntityObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../types";
import { invalidateEnumeration } from "./enumerations";

export const AGENT_QUERY_KEYS = {
  getPartnerIdNumber: () => ["getPartnerIdNumber"],
  importAgents: () => ["importAgents"],
  filterAgents: (filter?: AgentFilterPageRequest) => (filter ? ["filterAgents", filter] : ["filterAgents"]),
  downloadAgentsExport: () => ["downloadAgentsExport"],
  downloadSecondaryAgentsExport: () => ["downloadSecondaryAgentsExport"],
  getAgentTree: (filter?: AgentTreeFilterRequest) => (filter ? ["getAgentTree", filter] : ["getAgentTree"]),
  getAgentTreeNode: () => ["getAgentTreeNode"],
  searchAgent: () => ["searchAgent"],
  getAgent: (id: UUID) => ["getAgent", id],
  createAgent: () => ["createAgent"],
  updateAgent: () => ["updateAgent"],
  updateAgentParent: () => ["updateAgentParent"],
  deleteAgent: () => ["deleteAgent"],
  downloadProfilePicture: () => ["downloadProfilePicture"],
  updateProfilePicture: () => ["updateProfilePicture"],
  deleteProfilePicture: () => ["deleteProfilePicture"],
  uploadAgentAttachments: () => ["uploadAgentAttachments"],
  downloadAgentAttachmentsAsZip: () => ["downloadAgentAttachmentsAsZip"],
  deleteAgentAttachment: () => ["deleteAgentAttachment"],
  getSecondaryAgent: (id: UUID) => ["getSecondaryAgent", id],
  createSecondaryAgent: () => ["createSecondaryAgent"],
  updateSecondaryAgent: () => ["updateSecondaryAgent"],
  deleteSecondaryAgent: () => ["deleteSecondaryAgent"],
  getAgentBranding: (id: UUID) => ["getAgentBranding", id],
  createAgentBranding: () => ["createAgentBranding"],
  updateAgentBranding: () => ["updateAgentBranding"],
  deleteAgentBranding: () => ["deleteAgentBranding"],
  filterAgentEndingCompetences: (filter?: AgentCompetenceFilterPageRequest) =>
    filter ? ["filterAgentEndingCompetences", filter] : ["filterAgentEndingCompetences"],
  getAgentCompetences: (id?: UUID) => (id ? ["getAgentCompetences", id] : ["getAgentCompetences"]),
  createAgentCompetence: () => ["createAgentCompetence"],
  updateAgentCompetence: () => ["updateAgentCompetence"],
  deleteAgentCompetence: () => ["deleteAgentCompetence"],
  getAgentLicenses: (id: UUID) => ["getAgentLicenses", id],
  createAgentLicense: () => ["createAgentLicense"],
  updateAgentLicense: () => ["updateAgentLicense"],
  deleteAgentLicense: () => ["deleteAgentLicense"],
  getAgentBrokerNumbers: (id: UUID) => ["getAgentBrokerNumbers", id],
  createAgentBrokerNumber: () => ["createAgentBrokerNumber"],
  updateAgentBrokerNumber: () => ["updateAgentBrokerNumber"],
  deleteAgentBrokerNumber: () => ["deleteAgentBrokerNumber"],
};

export const useGetAgentPartnerIdNumber = () => {
  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.getPartnerIdNumber(),
    meta: { globalSpinner: false },
    mutationFn: async (payload?: AgentPartnerIdNumberRequest) => {
      const { data } = await agentApi.getAgentPartnerIdNumber(payload);

      return data;
    },
  });
};

export const useImportAgents = () => {
  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.importAgents(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: FormData) => {
      const { data } = await agentApi.importAgents(payload);

      return data;
    },
    onSuccess: (data) => {
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("agent.helpers.import.processStarted", { count: data }),
      });
    },
  });
};

export const useFilterAgents = (payload: AgentFilterPageRequest) => {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.filterAgents(payload),
    queryFn: async () => {
      const { data } = await agentApi.filterAgents(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useDownloadAgentsExport = () => {
  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.downloadAgentsExport(),
    mutationFn: async (payload: AgentFilterExportRequest) => {
      const response = await agentApi.downloadAgentsExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useDownloadSecondaryAgentsExport = () => {
  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.downloadSecondaryAgentsExport(),
    mutationFn: async (payload: AgentFilterExportRequest) => {
      const response = await agentApi.downloadSecondaryAgentsExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useGetAgentTree = (payload: AgentTreeFilterRequest) => {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.getAgentTree(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await agentApi.getAgentTree(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetAgentTreeNode = () => {
  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.getAgentTreeNode(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: AgentTreeFilterRequest & { rootAgentId: UUID }) => {
      const { data } = await agentApi.getAgentTree(payload);

      return data;
    },
  });
};

export const useSearchAgent = () => {
  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.searchAgent(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: AgentSearchRequest) => {
      const { data } = await agentApi.searchAgent(payload);

      return data;
    },
  });
};

export const useGetAgent = (id: UUID) => {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.getAgent(id),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await agentApi.getAgent({ id });

      return data;
    },
  });
};

export const useCreateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.createAgent(),
    mutationFn: async (payload: CreateUpdateAgent) => {
      const { data } = await agentApi.createAgent(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.filterAgents() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.updateAgent(),
    mutationFn: async (payload: EntityObject<CreateUpdateAgent>) => {
      const { data } = await agentApi.updateAgent(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.filterAgents() });
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgent(mutatedPayload.id), () => data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useUpdateParentAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.updateAgentParent(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateAgentParent>) => {
      const { data } = await agentApi.updateAgentParent(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.filterAgents() });
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgent(mutatedPayload.id), () => data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.deleteAgent(),
    mutationFn: (id: UUID) => {
      return agentApi.deleteAgent({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.filterAgents() });
      Notification.itemDeletedNotification();
    },
  });
};

export const useDownloadAgentProfilePicture = () => {
  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.downloadProfilePicture(),
    mutationFn: async (id: UUID) => {
      const response = await agentApi.downloadProfilePicture({ id });
      openBlobFile(response);

      return response.data;
    },
  });
};

export const useUpdateAgentProfilePicture = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.updateProfilePicture(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await agentApi.updateProfilePicture(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgent(mutatedPayload.id), (oldData: Agent) => ({
        ...oldData,
        profilePicture: data,
      }));
    },
  });
};

export const useDeleteAgentProfilePicture = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.deleteProfilePicture(),
    mutationFn: (id: UUID) => {
      return agentApi.deleteProfilePicture({ id });
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgent(mutatedPayload), (oldData: Agent) => ({
        ...oldData,
        profilePicture: undefined,
      }));
    },
  });
};

export const useUploadAgentAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.uploadAgentAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await agentApi.uploadAgentAttachments(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        AGENT_QUERY_KEYS.getAgent(mutatedPayload.id),
        (oldData: Agent) =>
          ({
            ...oldData,
            attachments: data,
          }) as Agent,
      );
    },
  });
};

export const useDownloadAgentAttachmentsAsZip = () => {
  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.downloadAgentAttachmentsAsZip(),
    mutationFn: async (payload: EntityIdObject) => {
      const response = await agentApi.downloadAgentAttachmentsAsZip(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useDeleteAgentAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.deleteAgentAttachment(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return agentApi.deleteAgentAttachment(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        AGENT_QUERY_KEYS.getAgent(mutatedPayload.id1),
        (oldData: Agent) =>
          ({
            ...oldData,
            attachments: oldData.attachments.filter((attachment) => attachment.id !== mutatedPayload.id2),
          }) as Agent,
      );
    },
  });
};

export const useGetSecondaryAgent = (id: UUID) => {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.getSecondaryAgent(id),
    queryFn: async () => {
      const { data } = await agentApi.getSecondaryAgent({ id });

      return data;
    },
  });
};

export const useCreateSecondaryAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.createSecondaryAgent(),
    mutationFn: async (payload: EntityObject<CreateUpdateSecondaryAgent>) => {
      const { data } = await agentApi.createSecondaryAgent(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getSecondaryAgent(mutatedPayload.id), data);
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateSecondaryAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.updateSecondaryAgent(),
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateSecondaryAgent>) => {
      const { data } = await agentApi.updateSecondaryAgent(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getSecondaryAgent(mutatedPayload.id1), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteSecondaryAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.deleteSecondaryAgent(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return agentApi.deleteSecondaryAgent(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.getSecondaryAgent(mutatedPayload.id1) });
      Notification.itemDeletedNotification();
    },
  });
};

export const useGetAgentBranding = (id: UUID) => {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.getAgentBranding(id),
    queryFn: async () => {
      const { data } = await agentApi.getAgentBranding({ id });

      return data;
    },
  });
};

export const useCreateAgentBranding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.createAgentBranding(),
    mutationFn: async (payload: EntityObject<CreateUpdateAgentBranding>) => {
      const { data } = await agentApi.createAgentBranding(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgentBranding(mutatedPayload.id), data);
      invalidateEnumeration(EnumerationType.BRANDING);
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAgentBranding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.updateAgentBranding(),
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateAgentBranding>) => {
      const { data } = await agentApi.updateAgentBranding(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgentBranding(mutatedPayload.id1), data);
      invalidateEnumeration(EnumerationType.BRANDING);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAgentBranding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.deleteAgentBranding(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return agentApi.deleteAgentBranding(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.getAgentBranding(mutatedPayload.id1) });
      invalidateEnumeration(EnumerationType.BRANDING);
      Notification.itemDeletedNotification();
    },
  });
};

export const useFilterAgentEndingCompetences = (payload: AgentCompetenceFilterPageRequest) => {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.filterAgentEndingCompetences(payload),
    queryFn: async () => {
      const { data } = await agentApi.filterAgentEndingCompetences(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetAgentCompetences = (id: UUID) => {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.getAgentCompetences(id),
    queryFn: async () => {
      const { data } = await agentApi.getAgentCompetences({ id });

      return data;
    },
    select: sortAgentCompetences,
  });
};

export const useCreateAgentCompetence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.createAgentCompetence(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateAgentCompetence>) => {
      const { data } = await agentApi.createAgentCompetence(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        AGENT_QUERY_KEYS.getAgentCompetences(mutatedPayload.id),
        (oldData: AgentCompetence[]) => [data, ...oldData],
      );
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.filterAgentEndingCompetences() });
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.getAgent(mutatedPayload.id) });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAgentCompetence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.updateAgentCompetence(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateAgentCompetence>) => {
      const { data } = await agentApi.updateAgentCompetence(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.getAgentCompetences() });
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.filterAgentEndingCompetences() });
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.getAgent(mutatedPayload.id1) });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAgentCompetence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.deleteAgentCompetence(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return agentApi.deleteAgentCompetence(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgentCompetences(mutatedPayload.id1), (oldData: AgentCompetence[]) =>
        oldData.filter((item) => item.id !== mutatedPayload.id2),
      );
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.filterAgentEndingCompetences() });
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.getAgent(mutatedPayload.id1) });
      Notification.itemDeletedNotification();
    },
  });
};

export const useGetAgentLicenses = (id: UUID) => {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.getAgentLicenses(id),
    queryFn: async () => {
      const { data } = await agentApi.getAgentLicenses({ id });

      return sortAgentLicenses(data);
    },
  });
};

export const useCreateAgentLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.createAgentLicense(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateAgentLicense>) => {
      const { data } = await agentApi.createAgentLicense(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgentLicenses(mutatedPayload.id), (oldData: AgentLicense[]) =>
        sortAgentLicenses([data, ...oldData]),
      );
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.getAgent(mutatedPayload.id) });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAgentLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.updateAgentLicense(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateAgentLicense>) => {
      const { data } = await agentApi.updateAgentLicense(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgentLicenses(mutatedPayload.id1), (oldData: AgentLicense[]) =>
        sortAgentLicenses(oldData.map((item) => (item.id === data.id ? data : item))),
      );
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.getAgent(mutatedPayload.id1) });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAgentLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.deleteAgentLicense(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return agentApi.deleteAgentLicense(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(AGENT_QUERY_KEYS.getAgentLicenses(mutatedPayload.id1), (oldData: AgentLicense[]) =>
        oldData.filter((item) => item.id !== mutatedPayload.id2),
      );
      queryClient.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.getAgent(mutatedPayload.id1) });
      Notification.itemDeletedNotification();
    },
  });
};

export const useGetAgentBrokerNumbers = (id: UUID) => {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.getAgentBrokerNumbers(id),
    queryFn: async () => {
      const { data } = await agentApi.getAgentBrokerNumbers({ id });

      return sortAgentBrokerNumbers(data);
    },
  });
};

export const useCreateAgentBrokerNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.createAgentBrokerNumber(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateAgentBrokerNumber>) => {
      const { data } = await agentApi.createAgentBrokerNumber(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        AGENT_QUERY_KEYS.getAgentBrokerNumbers(mutatedPayload.id),
        (oldData: AgentBrokerNumber[]) => sortAgentBrokerNumbers([data, ...oldData]),
      );
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAgentBrokerNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.updateAgentBrokerNumber(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateAgentBrokerNumber>) => {
      const { data } = await agentApi.updateAgentBrokerNumber(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        AGENT_QUERY_KEYS.getAgentBrokerNumbers(mutatedPayload.id1),
        (oldData: AgentBrokerNumber[]) =>
          sortAgentBrokerNumbers(oldData.map((item) => (item.id === data.id ? data : item))),
      );
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAgentBrokerNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_QUERY_KEYS.deleteAgentBrokerNumber(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return agentApi.deleteAgentBrokerNumber(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        AGENT_QUERY_KEYS.getAgentBrokerNumbers(mutatedPayload.id1),
        (oldData: AgentBrokerNumber[]) => oldData.filter((item) => item.id !== mutatedPayload.id2),
      );
      Notification.itemDeletedNotification();
    },
  });
};
